import { Tabs } from '@amelio/ui-library';
import { useMemo, useState } from 'react';

import { SummarySection } from './SummarySection';

import type { EmployeeEvolution } from '@/api/performance-profile/careerGrowthService';
import type { TranslationKey } from '@/i18n-key';

import { GROWTH_PANEL_SURFACE } from '@/components/CareerGrowth/careerGrowthStyles';
import { EvolutionBar } from '@/components/CareerGrowth/EvolutionBar';
import { computeSkillReadiness } from '@/components/CareerGrowth/skillReadiness';
import { SkillsGrid } from '@/components/CareerGrowth/SkillsGrid';
import { useAmelioTranslations } from '@/hooks';
import { usePortalEvolution } from '@/pages/EmployeePortal/hooks/usePortalReads';
import { getLocalizedText } from '@/utils/localization';

type ProfileView = 'current' | 'desired';

/** What each view of the journey reads and says; the desired profile lists only what is left. */
const VIEWS = {
    current: {
        label: 'employeePortalSummary.evolution.currentProfile',
        skillsTitle: 'employeePortalSummary.evolution.currentSkillsTitle',
        noProfile: 'employeePortalSummary.noCurrentProfile',
    },
    desired: {
        label: 'employeePortalSummary.evolution.desiredProfile',
        skillsTitle: 'employeePortalSummary.evolution.desiredSkillsTitle',
        noProfile: 'employeePortalSummary.evolution.noDesiredProfile',
    },
} as const satisfies Record<ProfileView, Record<string, TranslationKey>>;

const profileOf = (evolution: EmployeeEvolution, view: ProfileView) =>
    view === 'current' ? evolution.currentProfile : evolution.desiredProfile;

/**
 * "Votre parcours d'évolution": how far along the employee is on their current or their desired
 * profile, and the competencies that view asks of them. Read-only: the employee changes neither
 * profile here.
 */
export const EvolutionJourneySection = () => {
    const { t } = useAmelioTranslations();
    const evolution = usePortalEvolution();
    const [chosenView, setChosenView] = useState<ProfileView>();
    // Until the employee picks a view, the journey opens on what it can draw: the desired profile
    // when one is defined, the current one otherwise. Derived rather than stored, so the view
    // follows the journey once it answers instead of freezing on the render before it did.
    const view = chosenView ?? (evolution.data?.desiredProfile === null ? 'current' : 'desired');

    const tabs = (['current', 'desired'] as const).map((key) => {
        const profile = evolution.data && profileOf(evolution.data, key);
        return {
            key,
            label: (
                <span className={'flex flex-col'}>
                    <span>{t(VIEWS[key].label)}</span>
                    {profile && (
                        <span className={'text-[10px] font-normal'}>
                            {getLocalizedText(profile.name)}
                        </span>
                    )}
                </span>
            ),
        };
    });

    return (
        <SummarySection
            icon={'business-trend-up'}
            title={t('employeePortalSummary.evolution.title')}
            subtitle={t('employeePortalSummary.evolution.subtitle')}
            aside={
                <Tabs
                    variant={'segmented'}
                    items={tabs}
                    activeKey={view}
                    onChange={(key) => setChosenView(key === 'current' ? 'current' : 'desired')}
                    ariaLabel={t('employeePortalSummary.evolution.profileSwitch')}
                    className={'self-start'}
                />
            }
            evolution={evolution}
            dataTestId={'employee-portal-evolution'}
        >
            {(data) => (
                <EvolutionJourney
                    evolution={data}
                    view={view}
                />
            )}
        </SummarySection>
    );
};

const EvolutionJourney = ({
    evolution,
    view,
}: {
    evolution: EmployeeEvolution;
    view: ProfileView;
}) => {
    const { t } = useAmelioTranslations();

    const profile = profileOf(evolution, view);
    const readiness = useMemo(
        () => profile && computeSkillReadiness(profile.skills, evolution.proficiencyLevels),
        [profile, evolution.proficiencyLevels],
    );
    const skills = view === 'current' ? readiness?.items : readiness?.gaps;

    const readEmptyMessage = () => {
        if (!readiness) {
            return t(VIEWS[view].noProfile);
        }
        return readiness.items.length > 0
            ? t('employeePortalSummary.evolution.allReached')
            : t('employeePortalSummary.evolution.noSkills');
    };

    return (
        <>
            {/* Progress is measured towards a target: on the current profile the employee is already
                where they stand, so only its scores are shown. */}
            {view === 'desired' && (
                <EvolutionBar
                    score={readiness?.score ?? null}
                    employeeFirstName={null}
                    dataTestId={'employee-portal-evolution-bar'}
                />
            )}
            {skills && skills.length > 0 ? (
                <SkillsGrid
                    items={skills}
                    title={t(VIEWS[view].skillsTitle)}
                    dataTestId={'employee-portal-evolution-skills'}
                />
            ) : (
                <p className={`${GROWTH_PANEL_SURFACE} text-growth-slate px-6 py-6 text-[14px]`}>
                    {readEmptyMessage()}
                </p>
            )}
        </>
    );
};
